import React, { createContext, useContext, useState, useEffect, useCallback, useLayoutEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Valores por defecto del tema
const defaultTheme = {
  logo_url: null,
  sidebar_bg_color: "#1a1a2e",
  sidebar_text_color: "#ffffff",
  sidebar_hover_color: "#16213e",
  sidebar_hover_text_color: "#e3fe00",
  sidebar_active_color: "#0f3460",
  sidebar_active_text_color: "#e3fe00",
  primary_button_color: "#e3fe00",
  primary_button_text_color: "#000000",
  primary_button_hover_color: "#c9e100",
  primary_button_hover_text_color: "#000000",
  primary_button_gradient: true,
  primary_button_gradient_end: "#e9ce18",
  secondary_button_color: "#374151",
  secondary_button_text_color: "#ffffff",
  secondary_button_hover_color: "#4b5563",
  secondary_button_hover_text_color: "#ffffff",
  secondary_button_gradient: false,
  secondary_button_gradient_end: "#6b7280",
  button_glow_enabled: false,
  button_glow_color: "#e3fe00",
  chat_bubble_incoming_color: "#374151",
  chat_bubble_incoming_text_color: "#ffffff",
  chat_bubble_outgoing_color: "#e3fe00",
  chat_bubble_outgoing_text_color: "#000000",
  modal_border_color: "#e3fe00",
  modal_header_bg_color: "#1a1a2e",
  modal_header_text_color: "#ffffff",
  loading_color: "#e3fe00"
};

// Función para obtener el tema guardado en localStorage
const getSavedTheme = () => {
  try {
    const saved = localStorage.getItem('clientTheme');
    if (saved) {
      return { ...defaultTheme, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.warn('Error loading saved theme:', e);
  }
  return defaultTheme;
};

// Ya no aplicamos variables CSS globalmente
// Las variables se aplican localmente en ClientPortalWrapper
const applyThemeImmediately = (themeData, isAuthenticated = false) => {
  // No hacer nada - las variables se aplican en el wrapper
};

// Ya no aplicamos tema al cargar
// El tema se aplica solo dentro del ClientPortalWrapper

const ThemeContext = createContext({
  theme: defaultTheme,
  loading: true,
  updateTheme: async () => {},
  uploadLogo: async () => {},
  deleteLogo: async () => {},
  resetTheme: async () => {},
  refreshTheme: async () => {}
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getSavedTheme);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };
  
  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  const fetchTheme = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/theme`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        const newTheme = { ...defaultTheme, ...data };
        setTheme(newTheme);
        localStorage.setItem('clientTheme', JSON.stringify(newTheme));
        applyThemeToCSS(newTheme);
      }
    } catch (error) {
      console.warn('Error fetching theme (using cached):', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTheme = async (themeData) => {
    try {
      const response = await fetch(`${API_URL}/api/theme`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(themeData)
      });
      
      if (response.ok) {
        const data = await response.json();
        const newTheme = { ...defaultTheme, ...data };
        setTheme(newTheme);
        // Guardar en localStorage
        localStorage.setItem('clientTheme', JSON.stringify(newTheme));
        applyThemeToCSS(newTheme);
        return { success: true };
      }
      return { success: false, error: 'Error actualizando tema' };
    } catch (error) {
      console.error('Error updating theme:', error);
      return { success: false, error: error.message };
    }
  };

  const uploadLogo = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/theme/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setTheme(prev => ({ ...prev, logo_url: data.logo_url }));
        return { success: true, logo_url: data.logo_url };
      }
      let errorMsg = 'Error subiendo logo';
      try {
        const error = await response.json();
        errorMsg = error.detail || errorMsg;
      } catch {}
      return { success: false, error: errorMsg };
    } catch (error) {
      console.error('Error uploading logo:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteLogo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/theme/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setTheme(prev => ({ ...prev, logo_url: null }));
        return { success: true };
      }
      return { success: false, error: 'Error eliminando logo' };
    } catch (error) {
      console.error('Error deleting logo:', error);
      return { success: false, error: error.message };
    }
  };

  const resetTheme = async () => {
    try {
      const response = await fetch(`${API_URL}/api/theme/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setTheme(defaultTheme);
        localStorage.setItem('clientTheme', JSON.stringify(defaultTheme));
        applyThemeToCSS(defaultTheme);
        return { success: true };
      }
      return { success: false, error: 'Error reseteando tema' };
    } catch (error) {
      console.error('Error resetting theme:', error);
      return { success: false, error: error.message };
    }
  };

  const refreshTheme = async () => {
    await fetchTheme();
  };

  // Ya no necesitamos aplicar CSS globalmente
  // Las variables se aplican en el ClientPortalWrapper
  const applyThemeToCSS = (themeData) => {
    // No hacer nada - las variables se aplican localmente
  };

  useEffect(() => {
    // Solo cargar tema si hay token
    const token = localStorage.getItem('token');
    if (token) {
      fetchTheme();
    } else {
      setLoading(false);
    }
  }, [fetchTheme]);

  // Ya no necesitamos el useEffect para aplicar tema global
  // El tema se aplica localmente en ClientPortalWrapper

  return (
    <ThemeContext.Provider value={{
      theme,
      loading,
      updateTheme,
      uploadLogo,
      deleteLogo,
      resetTheme,
      refreshTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
